import { Controller } from "@hotwired/stimulus";
import { preloadEmojiImage } from "../helpers/emoji_image_cache";

export default class HelperController extends Controller {
  static targets = ["emojiShow"];

  initialize() {
    this.handleEmojiCopy = this.handleEmojiCopy.bind(this);
    this.handleEmojiSelection = this.handleEmojiSelection.bind(this);
    this.selectedEmojis = new Set();
    this.selectionFrame = null;
  }

  connect() {
    document.addEventListener("selectionchange", this.handleEmojiSelection);
    this.checkPostScrolling();
  }

  disconnect() {
    document.removeEventListener("selectionchange", this.handleEmojiSelection);
    if (this.selectionFrame) cancelAnimationFrame(this.selectionFrame);
    this.selectionFrame = null;
    this.clearEmojiSelection();
  }

  emojiShowTargetConnected(element) {
    if (element.dataset.emojiPending === "true") {
      this.preparePostEmojis(element);
      element.addEventListener("copy", this.handleEmojiCopy);
      return;
    }

    for (const emoji of element.querySelectorAll(".ql-emoji")) {
      const url = emoji.dataset.url;
      if (!url) continue;

      emoji.style.backgroundImage = `url("${url}")`;
      emoji.style.backgroundRepeat = "no-repeat";
      emoji.style.position = "relative";
    }

    element.addEventListener("copy", this.handleEmojiCopy);
  }

  async preparePostEmojis(element) {
    await Promise.all(Array.from(element.querySelectorAll(".ql-emoji"), async (emoji) => {
      const url = emoji.dataset.url;
      const loaded = await preloadEmojiImage(url);
      if (!element.isConnected) return;

      emoji.style.position = "relative";
      if (loaded) {
        emoji.style.backgroundImage = `url("${url}")`;
        emoji.style.backgroundSize = "20px 20px";
        emoji.style.backgroundPosition = "center";
        emoji.style.backgroundRepeat = "no-repeat";
      } else {
        emoji.style.backgroundImage = "";
      }

      const fallback = document.createElement("span");
      fallback.textContent = emoji.dataset.native || this.nativeEmoji(emoji.dataset.emoji) || "🙂";
      fallback.style.visibility = loaded ? "hidden" : "inherit";
      emoji.replaceChildren(fallback);
    }));

    if (!element.isConnected) return;
    element.style.visibility = "";
    element.dataset.emojiPending = "false";
    const content = element.closest("[data-post-content-pending=\"true\"]");
    if (content) {
      content.style.visibility = "";
      content.dataset.postContentPending = "false";
    }
  }

  emojiShowTargetDisconnected(element) {
    element.removeEventListener("copy", this.handleEmojiCopy);
  }

  handleEmojiCopy(event) {
    const selection = globalThis.getSelection();
    if (!selection || selection.rangeCount === 0 || !event.clipboardData) return;

    const fragment = selection.getRangeAt(0).cloneContents();
    const emojis = fragment.querySelectorAll?.(".ql-emoji") || [];
    if (emojis.length === 0) return;

    for (const emoji of emojis) {
      const nativeEmoji = emoji.dataset.native || this.nativeEmoji(emoji.dataset.emoji);
      emoji.replaceWith(document.createTextNode(nativeEmoji));
    }

    const container = document.createElement("div");
    container.append(fragment);

    event.preventDefault();
    event.clipboardData.setData("text/plain", this.plainText(container));
    event.clipboardData.setData("text/html", container.innerHTML);
  }

  handleEmojiSelection() {
    if (this.selectionFrame) return;

    this.selectionFrame = requestAnimationFrame(() => {
      this.selectionFrame = null;
      this.updateEmojiSelection();
    });
  }

  updateEmojiSelection() {
    const selection = globalThis.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const nextSelectedEmojis = new Set();

    if (!range || selection.isCollapsed) {
      this.clearEmojiSelection();
      return;
    }

    for (const element of this.selectionContainers(selection, range)) {
      for (const emoji of element.querySelectorAll(".ql-emoji")) {
        try {
          if (range.intersectsNode(emoji)) nextSelectedEmojis.add(emoji);
        } catch {
          // Ignore nodes removed by a Turbo render during selection.
        }
      }
    }

    for (const emoji of this.selectedEmojis) {
      if (!nextSelectedEmojis.has(emoji)) emoji.classList.remove("emoji-selected");
    }

    for (const emoji of nextSelectedEmojis) {
      emoji.classList.add("emoji-selected");
    }
    this.selectedEmojis = nextSelectedEmojis;
  }

  selectionContainers(selection, range) {
    const selector = "[data-helper-target~=\"emojiShow\"]";
    const start = selection.anchorNode?.parentElement?.closest(selector);
    const finish = selection.focusNode?.parentElement?.closest(selector);

    if (start && start === finish) return [start];

    return this.emojiShowTargets.filter((element) => {
      try {
        return range.intersectsNode(element);
      } catch {
        return false;
      }
    });
  }

  clearEmojiSelection() {
    for (const emoji of this.selectedEmojis) {
      emoji.classList.remove("emoji-selected");
    }
    this.selectedEmojis.clear();
  }

  nativeEmoji(unified) {
    if (!unified) return "";

    try {
      const codepoints = unified.split("-").map((value) => Number.parseInt(value, 16));
      return String.fromCodePoint(...codepoints);
    } catch (error) {
      console.warn("Unable to convert Unicode code points:", error);
      return "";
    }
  }

  plainText(container) {
    for (const br of container.querySelectorAll("br")) {
      br.replaceWith(document.createTextNode("\n"));
    }

    for (const block of container.querySelectorAll("p, div, li")) {
      if (!block.textContent.endsWith("\n")) block.append(document.createTextNode("\n"));
    }

    const text = container.textContent;
    let index = text.length;

    while (index > 0 && text[index - 1] === "\n") {
      index--;
    }

    return text.slice(0, index);
  }

  checkPostScrolling() {
    const match = /^\/posts\/(P[^/]+)$/.exec(globalThis.location.pathname);
    const match2 = /^\/comments\/(C[^/]+)$/.exec(globalThis.location.pathname);

    if (!match && !match2) return;
    if (document.querySelector("turbo-frame[busy]")) return;

    setTimeout(() => {
      if (document.querySelector(".modal.show")) return;

      const recordId = match ? match[1] : match2[1];
      let record = match
        ? document.getElementById(recordId)
        : document.getElementById(`comment_${recordId}`);

      if (!record) return;

      if (globalThis.location.href.includes("rl=true")) {
        record = record.nextElementSibling;
      }

      requestAnimationFrame(() => {
        const showPost = record.closest(".show-post");
        const frames = showPost?.querySelectorAll("turbo-frame") || [];
        const isFirstPost = frames[0] === record.closest("turbo-frame");

        if (!isFirstPost) {
          record.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }

        if (!globalThis.location.href.includes("hl=true")) return;

        record.classList.add("record-highlight");

        record.addEventListener(
          "animationend",
          () => record.classList.remove("record-highlight"),
          { once: true }
        );
      });
    }, 300);
  }

  searchToggle(event) {
    event?.currentTarget?.form?.classList?.toggle("is-expanded");

    if (event?.currentTarget?.form?.classList?.contains("is-expanded")) {
      this.element.querySelector(".comic-search__input")?.focus();
    }
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  historyBack(event) {
    const navigation = this.application.getControllerForElementAndIdentifier(
      document.body,
      "navigation-history"
    );

    if (navigation) navigation.back(event);
  }

  setGifForm(event) {
    const gifEnabled = document.querySelector("[data-gif-enabled]");
    const target = event.currentTarget;
    const form = target.closest("form") || document.getElementById("new_post_form");
    const index = target.dataset.index;
    const gifModal = document.getElementById("addGifModal");
    if (gifModal) {
      gifModal.dataset.unsavedChangesPostFormValue = String(
        Boolean(form?.matches(".edit-post-details"))
      );
    }
    form.dataset.filepondId = index ? `post_medias_${index}` : (form.querySelector(".filepond--root ")?.id || "message_attachments");

    if (gifEnabled) {
      delete gifEnabled.dataset.gifEnabled;
    }

    if (form) {
      form.dataset.gifEnabled = "true";
    }
  }
}
